import { Interpolate, Message } from '@wg/wows-react-uikit';
import classnames from 'classnames';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { playButtonClickSound } from '@wg/web2clientapi/sound';
import styles from './StartBundle.scss';
import type { IReward } from '~/types';
import { useAppSelector } from '~/hooks';
import RewardsDialog from '~/Components/Dialogs/RewardsDialog/RewardsDialog';
import { gold as wowsEntityGold } from '~/wowsEntities';
import { isInIframe } from '~/utils';
import { armoryHideBlur, armoryShowBlur } from '~/armory';
import { identificationDevice } from '~/utils/indetificationDevice';
import FooterStartBundle from '~/Components/StartBundle/FooterStartBundle';
import Text from '~/Components/Text/Text';

export interface StartBundle_Props {
    index: number,
    count?: number,
    price: number,
    isBought: boolean,
    notEnoughMoney: boolean,
    needGold: number,
    isLocked?: boolean,
    rewards: Array<IReward>,
    onBuy?: () => void,
    title?: string,
    description?: string,
    allBoostersCount?: number,
}

const StartBundle = (props: StartBundle_Props) => {
    const {
        index, count, price, isBought, notEnoughMoney, needGold, isLocked,
        rewards, onBuy, title, description, allBoostersCount
    } = props;
    const appState = useAppSelector((state) => state.app);
    const { t } = useTranslation();
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [isMobileDevice] = useState(identificationDevice(navigator.userAgent));
    const inIframe = isInIframe();
    const { pendingTransactions } = appState.account;
    const wrapper = classnames(styles.wrapper, {
        [styles.wrapperFrame]: inIframe,
        [styles.first]: index === 0,
        [styles.second]: index === 1,
        [styles.third]: index === 2,
        [styles.locked]: !!isLocked,
        [styles.bought]: isBought,
    });

    const isBuyButtonDisabled = notEnoughMoney || isBought || isLocked || pendingTransactions;
    const bundleRewardsIds = rewards.map((r) => r.id);

    const subtitle = () => {
        if (Number(count) > 1) {
            return (
                <Interpolate
                    str={t('%(bonus)s этапа строительства', {
                        count,
                    })}
                    bonus={count}
                />
            );
        }
        return t('Любое количество этапов');
    };

    const getDescription = () => {
        if (index + 1 <= Number(allBoostersCount)) {
            return (
                <>
                    <Text str={description || ''} />
                    <span className={styles.descriptionBlock}>{t('Приобретая Стартовый набор, вы получаете награды за все завершаемые этапы.')}</span>
                </>
            );
        }
        return <Text str={description || ''} />;
    };

    const openBuyDialog = useCallback(() => {
        setIsDialogVisible(true);
        if (inIframe) {
            armoryShowBlur();
        }
    }, [inIframe]);

    const onClickHandler = useCallback(() => {
        playButtonClickSound();
        openBuyDialog();
    }, [openBuyDialog]);

    const onRewardsDialogHide = useCallback(() => {
        setIsDialogVisible(false);
        armoryHideBlur();
    }, []);

    const onRewardsDialogActionButtonPress = useCallback(() => {
        setIsDialogVisible(false);
        armoryHideBlur();
        if (onBuy) {
            onBuy();
        }
    }, [onBuy]);

    return (
        <>
            <div
                className={wrapper}
                onClick={
                    isBuyButtonDisabled
                        ? undefined
                        : onClickHandler
                }
            >
                <div className={styles.title}>{title}</div>
                <div className={styles.subtitle}>{subtitle()}</div>
                <div className={styles.fixedHeightContent}>
                    <div className={styles.description}>{getDescription()}</div>
                    {isBought ? (
                        <div className={styles.boughtMessage}>
                            <Message style={'inline'} type={'done'}>
                                {t('Получено')}
                            </Message>
                        </div>
                    ) : null}
                </div>
                <FooterStartBundle
                    isMobileDevice={isMobileDevice}
                    isLocked={!!isLocked}
                    isBought={isBought}
                    count={Number(count)}
                    isBuyButtonDisabled={!!isBuyButtonDisabled}
                    needGold={needGold}
                    notEnoughMoney={notEnoughMoney}
                    openBuyDialog={openBuyDialog}
                    price={price}
                />
            </div>
            <RewardsDialog
                isVisible={isDialogVisible}
                ids={bundleRewardsIds}
                onHide={onRewardsDialogHide}
                onActionButtonPress={onRewardsDialogActionButtonPress}
                title={title as string}
                subtitle={t('Приобретая Стартовый набор, вы получаете награды за все завершаемые этапы.')}
                actionButtonText={t('Приобрести')}
                isBuyAction
                footerContent={
                    <div className={styles.dialogPrice}>
                        <Interpolate str={t('Приобрести набор %(bundleName)s за %(price)s?')} bundleName={title} price={wowsEntityGold(price)} />
                    </div>
                }
            />
        </>
    );
};

export default StartBundle;
